// In-place add for the cart add-on form (snippets/dk-cart-addon.liquid).
// Mirrors Horizon's product-form add: announce a CartLinesUpdateEvent, post to
// /cart/add.js with the cart sections, then resolve the event so the drawer,
// cart page and header count re-render. Without this script the form still
// posts normally and lands on the cart page.
import { CartLinesUpdateEvent } from '@shopify/events';

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-dk-addon')) return;
  event.preventDefault();

  const button = form.querySelector('button');
  if (button) button.disabled = true;

  const variantId = Number(new FormData(form).get('id'));
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  const deferred = CartLinesUpdateEvent.createPromise();

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const added = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _source: form.dataset.dkSource || 'drawer-addon' } }], sections: sectionIds.join(',') }),
    }).then((response) => response.json());
    if (added.status) throw new Error(added.description || added.message || 'Add to cart failed');

    const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((response) =>
      response.json()
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'dk-cart-addon', itemCount: 1, sections: added.sections, didError: false },
    });
  } catch (error) {
    deferred.reject(error);
    window.location.href = Theme.routes.cart_url;
  }
});

// 13 Oct 2026: basket email box (snippets/dk-cart-email.liquid) and the bed + robe set
// (snippets/dk-assure.liquid). Same file so one module load covers the drawer and the pages.

const EMAIL_DONE_KEY = 'dk-cart-email-done';
const showEmailDone = () => {
  document.querySelectorAll('[data-dk-cart-email]').forEach((form) => {
    const ask = form.querySelector('.dk-cart-email__ask');
    const ok = form.querySelector('.dk-cart-email__ok');
    if (ask) ask.hidden = true;
    if (ok) ok.hidden = false;
  });
};
let emailWatched = false;
const watchEmailDone = () => {
  showEmailDone();
  if (emailWatched) return;
  emailWatched = true;
  // The drawer re-renders its HTML after every cart change, so re-apply the done state.
  new MutationObserver(showEmailDone).observe(document.body, { childList: true, subtree: true });
};
const markEmailDone = () => {
  try {
    localStorage.setItem(EMAIL_DONE_KEY, '1');
  } catch (error) {
    /* storage blocked: the box just shows again next time */
  }
  watchEmailDone();
};
// 14 Oct 2026: the basket box promises the code is applied now, so apply it on the return.
const applyWelcome = () => fetch('/discount/WELCOME10?redirect=' + encodeURIComponent(location.pathname), { credentials: 'same-origin' }).catch(() => {});
// The form posts as a normal page load: Shopify answers 403 to fetch and iframe posts of the
// customer form, and its spam protection (storefront-forms-hcaptcha) cancels the first submit,
// runs hCaptcha, then posts the form itself and comes back to the referring page. The flag that
// says "posted" is set on pagehide within a minute of a submit, so an abandoned captcha does not
// count. (Shopify's ?_su_rec is Shop's own sign-in param, not a success marker.)
const EMAIL_PENDING_KEY = 'dk-cart-email-pending';
let emailSubmitAt = 0;
let emailDone = false;
let justPosted = false;
try {
  const query = new URLSearchParams(location.search);
  justPosted = query.get('dk-email') === 'ok' || query.get('customer_posted') === 'true' || sessionStorage.getItem(EMAIL_PENDING_KEY) === '1';
  sessionStorage.removeItem(EMAIL_PENDING_KEY);
  if (justPosted) {
    localStorage.setItem(EMAIL_DONE_KEY, '1');
    applyWelcome();
  }
  emailDone = justPosted || localStorage.getItem(EMAIL_DONE_KEY) === '1';
} catch (error) {
  emailDone = justPosted;
}
if (emailDone) watchEmailDone();
let reopenDrawer = false;
try {
  reopenDrawer = sessionStorage.getItem('dk-open-drawer') === '1';
  sessionStorage.removeItem('dk-open-drawer');
} catch (error) {
  reopenDrawer = false;
}
if ((justPosted || reopenDrawer) && !/\/cart\b/.test(location.pathname)) {
  const openDrawer = () => document.querySelector('[on\\:click="#cart-drawer/toggle"]')?.click();
  if (document.readyState === 'complete') setTimeout(openDrawer, 300);
  else window.addEventListener('load', () => setTimeout(openDrawer, 300), { once: true });
}

// 14 Oct 2026: one-tap swap of a round Calm Bed line for the UK-stock Oval (snippets/dk-cart-delivery.liquid).
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-dk-swap')) return;
  event.preventDefault();
  const button = form.querySelector('button');
  if (button) button.disabled = true;
  try {
    const changed = await fetch(`${Theme.routes.cart_url}/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: form.dataset.dkLine, quantity: 0 }),
    }).then((response) => response.json());
    if (changed.status) throw new Error(changed.description || 'Could not change the basket');
    const added = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(form.dataset.dkVariant), quantity: 1, properties: { _source: 'drawer-swap' } }] }),
    }).then((response) => response.json());
    if (added.status) throw new Error(added.description || 'Could not add the Oval');
    try {
      sessionStorage.setItem('dk-open-drawer', '1');
    } catch (error) {
      /* ignore */
    }
    window.location.reload();
  } catch (error) {
    if (button) {
      button.disabled = false;
      const strong = button.querySelector('strong');
      if (strong) strong.textContent = 'Could not swap, try again';
    }
  }
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-dk-cart-email')) return;
  emailSubmitAt = Date.now();
  const button = form.querySelector('button');
  if (button && !button.disabled) {
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Saving';
    setTimeout(() => {
      button.disabled = false;
      button.textContent = label;
    }, 10000);
  }
});
window.addEventListener('pagehide', () => {
  if (Date.now() - emailSubmitAt < 60000) {
    try {
      sessionStorage.setItem(EMAIL_PENDING_KEY, '1');
    } catch (error) {
      /* ignore */
    }
  }
});

const bedVariantId = (box) => {
  const fromUrl = new URLSearchParams(location.search).get('variant');
  if (fromUrl) return fromUrl;
  const input = document.querySelector('product-form-component input[name="id"]');
  return (input && input.value) || box.dataset.dkBedDefault;
};
const refreshSetTotals = () => {
  document.querySelectorAll('[data-dk-set]').forEach((box) => {
    let prices = {};
    try {
      prices = JSON.parse(box.dataset.dkBedPrices || '{}');
    } catch (error) {
      return;
    }
    const select = box.querySelector('select');
    const total = box.querySelector('[data-dk-set-total]');
    const bed = prices[bedVariantId(box)];
    const option = select && select.selectedOptions[0];
    const robe = option && Number(option.dataset.price);
    if (!total || !bed || !robe) return;
    const extra = [...box.querySelectorAll('input[type=checkbox]:checked')].reduce((sum, c) => sum + Number(c.dataset.price || 0), 0);
    total.textContent = `${box.dataset.dkCurrency || '£'}${((bed + robe - 800 + extra) / 100).toFixed(2)}`;
  });
};
if (document.querySelector('[data-dk-set]')) {
  refreshSetTotals();
  document.addEventListener('change', () => setTimeout(refreshSetTotals, 50));
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      refreshSetTotals();
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['value'] });
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const box = form.closest('[data-dk-set]');
  if (!box) return;
  event.preventDefault();
  const button = form.querySelector('button');
  if (button) button.disabled = true;
  const robe = Number(new FormData(form).get('id'));
  const bed = Number(bedVariantId(box));
  const extras = [...box.querySelectorAll('input[type=checkbox]:checked')].map((c) => ({ id: Number(c.value), quantity: 1 }));
  try {
    const added = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: bed, quantity: 1 }, { id: robe, quantity: 1 }, ...extras] }),
    }).then((response) => response.json());
    if (added.status) throw new Error(added.description || added.message || 'Add to cart failed');
    window.location.href = Theme.routes.cart_url;
  } catch (error) {
    if (button) {
      button.disabled = false;
      button.textContent = 'Could not add, try again';
    }
  }
});
